import type { InvitationDto, ProjectMemberDto, WorkspaceCandidateDto } from '@project-os/contracts';
import type {
  AddProjectMemberInput,
  InviteProjectMemberInput,
  UpdateProjectMemberInput,
} from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';
import { projectKeys } from './projects';

export const memberKeys = {
  list: (projectId: string) => ['projects', projectId, 'members'] as const,
  candidates: (projectId: string) => ['projects', projectId, 'member-candidates'] as const,
};

export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: memberKeys.list(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ProjectMemberDto[]>(`/projects/${projectId}/members`, { signal }),
    enabled: Boolean(projectId),
  });
}

/** Участники рабочего пространства, доступные для добавления. */
export function useMemberCandidates(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: memberKeys.candidates(projectId),
    queryFn: ({ signal }) =>
      apiRequest<WorkspaceCandidateDto[]>(`/projects/${projectId}/members/candidates`, { signal }),
    enabled,
  });
}

/** Все изменения состава возвращают новый список — кэш обновляется ответом. */
function useMemberMutation<TInput>(
  projectId: string,
  request: (input: TInput) => Promise<ProjectMemberDto[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (members) => {
      queryClient.setQueryData(memberKeys.list(projectId), members);
      void queryClient.invalidateQueries({ queryKey: memberKeys.candidates(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useAddMember(projectId: string) {
  return useMemberMutation(projectId, (input: AddProjectMemberInput) =>
    apiRequest<ProjectMemberDto[]>(`/projects/${projectId}/members`, {
      method: 'POST',
      body: input,
    }),
  );
}

/**
 * Приглашение нового человека.
 *
 * Ответ содержит одноразовую ссылку, поэтому в кэш он не кладётся: ссылку
 * показывают один раз и тут же забывают. Обновляется только список
 * участников — приглашённый появляется в нём сразу.
 */
export function useInviteMember(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteProjectMemberInput) =>
      apiRequest<InvitationDto>(`/projects/${projectId}/members/invite`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.list(projectId) });
      void queryClient.invalidateQueries({ queryKey: memberKeys.candidates(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useUpdateMemberRole(projectId: string) {
  return useMemberMutation(projectId, (input: UpdateProjectMemberInput & { memberId: string }) =>
    apiRequest<ProjectMemberDto[]>(`/projects/${projectId}/members/${input.memberId}`, {
      method: 'PATCH',
      body: { role: input.role },
    }),
  );
}

export function useRemoveMember(projectId: string) {
  return useMemberMutation(projectId, (memberId: string) =>
    apiRequest<ProjectMemberDto[]>(`/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
    }),
  );
}
